// ═══════════════════════════════════════════════════════════════════════════
// useAuthConfig — Lee la config de autenticación (configuracion/auth)
// ═══════════════════════════════════════════════════════════════════════════
// Gobierna qué MÉTODOS de login se ofrecen (Google / contraseña) y, para Google,
// si es solo-Milbrands (con `hd` de Workspace) o para todos. NO decide quién
// elige el método (eso se resuelve en el alta admin o en el registro
// self-service) — solo qué está DISPONIBLE.
//
// Se lee SIN login (el login la consulta antes de autenticar) → la regla de
// `configuracion/auth` es read público. Si no existe el doc o falla la lectura,
// cae a AUTH_CONFIG_DEFAULT (= comportamiento actual: Google solo-milbrands +
// contraseña). Se lee una vez y queda en cache de memoria.
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@core/lib/firebase'
import { AUTH_CONFIG_DEFAULT } from '@core/lib/constants'

let _cache = null

// Permite invalidar el cache desde AdminPage al guardar la config.
export function clearAuthConfigCache() { _cache = null }

/**
 * Normaliza el doc crudo de Firestore a la forma canónica, cayendo a los
 * defaults campo por campo (tolerante a docs parciales). PURO — exportado para
 * test sin Firestore.
 */
export function normalizarAuthConfig(data) {
  const d = data || {}
  const googleSSO = ['solo-milbrands', 'todos', 'off'].includes(d.googleSSO)
    ? d.googleSSO
    : AUTH_CONFIG_DEFAULT.googleSSO
  return {
    googleSSO,
    passwordEnabled: d.passwordEnabled !== false, // default true
    googleHd: typeof d.googleHd === 'string' && d.googleHd ? d.googleHd : AUTH_CONFIG_DEFAULT.googleHd,
    // Auto-registro con Google (en /registro). Desacoplado de googleSSO: el
    // LOGIN puede estar 'todos' (sin hd) sin abrir el alta self-service con
    // Google. Default false.
    googleRegistro: d.googleRegistro === true,
  }
}

/**
 * ¿Se muestra el botón de Google en el login? (derivado, PURO)
 * @param {Object} cfg — config normalizada
 * @returns {boolean}
 */
export function googleHabilitado(cfg) {
  return (cfg?.googleSSO || 'off') !== 'off'
}

/**
 * ¿Se muestra el botón de Google en el AUTO-REGISTRO (/registro)? (derivado, PURO)
 * Desacoplado de googleSSO: requiere el flag explícito googleRegistro + que
 * Google no esté 'off'. Permite LOGIN con Google abierto sin liberar el alta
 * self-service con Google.
 * @param {Object} cfg — config normalizada
 * @returns {boolean}
 */
export function registroGoogleHabilitado(cfg) {
  return cfg?.googleRegistro === true && (cfg?.googleSSO || 'off') !== 'off'
}

/**
 * Dominio `hd` a pasarle a Google al login (PURO). 'solo-milbrands' restringe al
 * dominio Workspace configurado; 'todos'/'off' → null (sin restricción). Sin
 * hardcode del dominio: sale de cfg.googleHd.
 * @param {Object} cfg — config normalizada
 * @returns {string|null}
 */
export function hdParaGoogle(cfg) {
  return cfg?.googleSSO === 'solo-milbrands' ? (cfg?.googleHd || null) : null
}

export function useAuthConfig() {
  const [cfg, setCfg] = useState(_cache || { ...AUTH_CONFIG_DEFAULT, loaded: !!_cache })

  useEffect(() => {
    if (_cache) return
    let cancelled = false

    getDoc(doc(db, 'configuracion', 'auth'))
      .then(snap => {
        if (cancelled) return
        _cache = { ...normalizarAuthConfig(snap.exists() ? snap.data() : {}), loaded: true }
        setCfg(_cache)
      })
      .catch(() => {
        if (cancelled) return
        _cache = { ...AUTH_CONFIG_DEFAULT, loaded: true }
        setCfg(_cache)
      })

    return () => { cancelled = true }
  }, [])

  return cfg
}
